import { HeatmapLayer, LayerCollection, MapProperties } from '@datum-map';
import {
  Layer,
  LayerLayerTypeEnum,
} from '@datum-group/isogd-openapi/lib/models/layer';
import {
  observable,
  action,
  makeObservable,
  computed,
  runInAction,
  reaction,
} from 'mobx';
import VectorLayer from 'ol/layer/Vector';
import olHeatmapLayer from 'ol/layer/Heatmap';
import * as olProj from 'ol/proj';
import { LayerStyle } from './Legend/StyleTypes';
import { TableSelector } from './shared/common';
import { TableSelectorServices } from './shared/common/TableSelector';
import {
  createHeatMapPdf,
  createHeatMapScreenshot,
} from '@project/components/Module/HeatMap/utils';
import { DocumentOrientation } from '@project/components/Module/HeatMap/types';
import { SettingsStore } from '@project/store/Settings';
import { BaseLayersStore } from '@project/store/BaseLayersStore';
import TileLayer from 'ol/layer/Tile';

export interface HeatmapStyles {
  radius: number;
  blur: number;
  gradient: string[];
}

const QUERY = { geometry_type: 'Point' };
const DEFAULT_HEATMAP_STYLE: HeatmapStyles = {
  radius: 10,
  blur: 10,
  gradient: ['#0000ff', '#00ffff', '#00ff00', '#ffff00', '#ff0000'],
};

export class HeatmapLayerCreator {
  @observable selector: TableSelector;
  @observable private _heatmapStyles: HeatmapStyles;
  @observable private _isLoading: boolean = false;
  @observable private _error?: string = undefined;
  @observable private _layerCreated: boolean = false;
  @observable description: string = '';
  mapProperties: MapProperties;
  baseLayers: BaseLayersStore;
  private _initHeatmapStyle: HeatmapStyles;
  private _services: TableSelectorServices;
  private _layerCollection: LayerCollection;
  private _appSettings: SettingsStore;

  constructor(
    services: TableSelectorServices,
    layerCollection: LayerCollection,
    mapProperties: MapProperties,
    appSettings: SettingsStore,
    baseStore: BaseLayersStore,
    initialHeatmapStyles?: HeatmapStyles,
  ) {
    this.mapProperties = mapProperties;
    this._layerCollection = layerCollection;
    this._services = services;
    this._appSettings = appSettings;
    this.selector = new TableSelector(services, QUERY);
    this.description = '';
    this.baseLayers = baseStore;
    this._initHeatmapStyle = this._getCopy(
      initialHeatmapStyles || DEFAULT_HEATMAP_STYLE,
    );
    this._heatmapStyles = this._getCopy(this._initHeatmapStyle);

    makeObservable(this);

    reaction(
      () => this.selector.value,
      (newValue) => {
        this.setDescription(newValue?.description ? newValue?.description : '');
      },
    );
  }

  @computed get isLoading() {
    return this._isLoading;
  }

  @computed get isLayerCreated() {
    return this._layerCreated;
  }

  @computed get error() {
    return this._error;
  }

  @computed get heatmapStyles() {
    return this._getCopy(this._heatmapStyles);
  }

  @action setDescription = (value: string) => {
    this.description = value;
  };

  @action setBlur = (value: number) => {
    this._heatmapStyles.blur = value;
  };

  @action setRadius = (value: number) => {
    this._heatmapStyles.radius = value;
  };

  @action setGradient = (index: number, value: string) => {
    this._heatmapStyles.gradient[index] = value;
  };

  @action clear = () => {
    this.selector = new TableSelector(this._services, QUERY);
    this._heatmapStyles = this._getCopy(this._initHeatmapStyle);
  };

  @action submit = () => {
    const { value } = this.selector;

    if (value === undefined) {
      throw new Error('Слой для создания тепловой карты не выбран');
    }
    this._isLoading = true;
    this._error = undefined;

    const heatmapAmount = this._getHeatmapLayerAmountOnMap();
    const tableName = value.table_name;
    const description = value.description ?? value.table_name;
    const heatmapLayer = this._createHeatmapLayer(
      tableName,
      description,
      this._heatmapStyles,
      heatmapAmount,
    );

    try {
      this._layerCollection.add(heatmapLayer);
      this._layerCreated = true;
    } catch (e) {
      runInAction(() => (this._error = 'Не удалось добавить слой на карту'));
    } finally {
      runInAction(() => (this._isLoading = false));
    }
  };

  private _getCopy(style: HeatmapStyles): HeatmapStyles {
    return { ...style, gradient: [...style.gradient] };
  }

  private _createHeatmapLayer(
    tableName: string,
    description: string,
    style: { radius: number; blur: number; gradient: string[] },
    heatmapAmount: number,
  ) {
    const styleCopy = this._getCopy(style);

    const heatmapStyle: LayerStyle = {
      default: {
        point: LayerStyle.DEFAULT_POINT_STYLE,
      },
      conditional: [],
      heatmap: {
        ...styleCopy,
      },
    };

    const layer: Layer = {
      data_source: tableName,
      layer_name: `HEATMAP-${tableName}-${heatmapAmount + 1}`,
      description: description,
      layer_type: LayerLayerTypeEnum.Table,
      name: `HEATMAP-${tableName}-${heatmapAmount + 1}`,
      // @ts-ignore
      style: heatmapStyle,
    };

    return layer;
  }

  private _getHeatmapLayerAmountOnMap() {
    let amount = 0;

    this._layerCollection.layers.forEach((layer) => {
      if (layer instanceof HeatmapLayer) amount++;
    });

    return amount;
  }

  private getLayer = () => {
    const { value } = this.selector;

    const heatmapAmount = this._getHeatmapLayerAmountOnMap();
    const tableName = value?.table_name;

    return this.mapProperties.map
      .getLayers()
      .getArray()
      .find((layer) => {
        if (layer.get('name')) {
          return layer.get('name') === `HEATMAP-${tableName}-${heatmapAmount}`;
        } else {
          return false;
        }
      }) as VectorLayer;
  };

  public getHeatMapScreenshot = async () => {
    const heatLayer = this.getLayer() as unknown as olHeatmapLayer;

    if (!heatLayer) {
      throw new Error('Слоя тепловой карты не существует');
    }

    const extent = this._appSettings.data.default_map_extent
      ? olProj.transformExtent(
          this._appSettings.data.default_map_extent,
          'EPSG:4326',
          this.mapProperties.map.getView().getProjection(),
        )
      : undefined;

    const baseLayerName = this.baseLayers.getActiveLayer()?.layer_name;

    let baseLayer: TileLayer | undefined = undefined;

    this.mapProperties.map.getLayers().forEach((layer) => {
      if (layer instanceof TileLayer && layer.get('name') === baseLayerName) {
        baseLayer = layer;
      }
    });

    if (!baseLayer) {
      throw new Error('Не удалось получить слой картографической основы');
    }

    return await createHeatMapScreenshot(
      heatLayer,
      baseLayer,
      this._heatmapStyles,
      extent,
    );
  };

  handlePdfDownload = async (orientation: DocumentOrientation) => {
    const screenshot = await this.getHeatMapScreenshot();

    const styles = this.heatmapStyles;

    const res = createHeatMapPdf({
      title: this.description ?? '',
      screenshot,
      styles: styles,
      orientation,
    });
    res.download();

    return res;
  };

  handlePdfPrint = async (orientation: DocumentOrientation) => {
    const screenshot = await this.getHeatMapScreenshot();

    const styles = this.heatmapStyles;

    const res = createHeatMapPdf({
      title: this.description ?? '',
      screenshot,
      styles: styles,
      orientation,
    });
    res.print();

    return res;
  };
}
